import { useMemo, useState, useCallback, useEffect } from "react";
import { aboutPageStats } from "../data/constants";
import {
  getPartnersPage,
  listPartnerCategories,
  listPartners,
} from "../data/cmsStore";
import { fetchPublicCmsData } from "../api/cmsApi";
import AnimatedCounter from "../components/AnimatedCounter";
import SiteCta from "../components/SiteCta";

const baseUrl = process.env.PUBLIC_URL || "";

function resolveImage(src) {
  if (!src) return null;
  if (src.startsWith("data:") || src.startsWith("http://") || src.startsWith("https://")) {
    return src;
  }
  return `${baseUrl}${src.startsWith("/") ? src : `/${src}`}`;
}

export default function PartnersPage() {
  const [activePartner, setActivePartner] = useState(null);
  const page = useMemo(() => getPartnersPage(), []);
  const [categories, setCategories] = useState(() => listPartnerCategories());
  const [partners, setPartners] = useState(() =>
    listPartners().filter((p) => p.status !== "hidden")
  );
  const [loading, setLoading] = useState(true);
  const [fromDb, setFromDb] = useState(false);

  useEffect(() => {
    (async () => {
      const localCats = listPartnerCategories();
      const localPartners = listPartners().filter((p) => p.status !== "hidden");
      try {
        const data = await fetchPublicCmsData();
        const apiCats = Array.isArray(data.partnerCategories) ? data.partnerCategories : [];
        const apiPartners = Array.isArray(data.partners)
          ? data.partners.filter((p) => p.status !== "hidden")
          : [];
        // Use DB when seeded (enough rows). Otherwise keep full local logos.
        const dbReady = apiPartners.length >= 20 || apiPartners.length >= localPartners.length;
        if (dbReady) {
          setCategories(apiCats);
          setPartners(apiPartners);
          setFromDb(true);
        } else {
          setCategories(localCats);
          setPartners(localPartners);
          setFromDb(false);
        }
      } catch (_) {
        setCategories(localCats);
        setPartners(localPartners);
        setFromDb(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(
    () =>
      categories
        .map((cat) => ({
          ...cat,
          logos: partners
            .filter((p) => p.categoryId === cat.id)
            .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        }))
        .filter((cat) => cat.logos.length > 0),
    [categories, partners]
  );

  const showPartner = useCallback((partner) => setActivePartner(partner), []);
  const hidePartner = useCallback(() => setActivePartner(null), []);

  const introImg = resolveImage(page.introImage);

  return (
    <div className="partners-page">
      <section className="partners-page-hero animate-on-scroll">
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-center">
            <div className="col-lg-7">
              <span className="partners-page-label">{page.heroLabel}</span>
              <h1 className="partners-page-hero-title">
                {page.heroTitleBefore}{" "}
                <span className="partners-page-accent">{page.heroTitleAccent}</span>
              </h1>
              <p className="partners-page-hero-desc">{page.heroDesc}</p>
            </div>
            <div className="col-lg-5">
              <div className="partners-page-stats-card">
                {aboutPageStats.map((stat, i) => (
                  <div
                    key={stat.label}
                    className={`partners-page-stat${i % 2 === 0 ? " has-right-border" : ""}${i < 2 ? " has-bottom-border" : ""}`}
                  >
                    <AnimatedCounter
                      value={stat.value}
                      suffix={stat.suffix}
                      className="partners-page-stat-num"
                      as="div"
                    />
                    <span className="partners-page-stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="partners-page-intro animate-on-scroll">
        <div className="container">
          <div className="partners-page-intro-card">
            <div className="row g-4 g-lg-5 align-items-center">
              <div className="col-lg-5">
                <div className="partners-page-intro-visual">
                  {introImg ? (
                    <img
                      src={introImg}
                      alt="Emicron technology partners"
                      className="partners-page-intro-img"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextElementSibling?.classList.remove("d-none");
                      }}
                    />
                  ) : null}
                  <div className={`partners-page-intro-placeholder${introImg ? " d-none" : ""}`}>
                    <i className="bi bi-award" aria-hidden="true"></i>
                    <span>Our Partners</span>
                  </div>
                </div>
              </div>
              <div className="col-lg-7">
                <span className="partners-page-label">{page.introLabel}</span>
                <h2 className="partners-page-intro-title">{page.introTitle}</h2>
                <p className="partners-page-intro-text">{page.introText1}</p>
                <p className="partners-page-intro-text mb-0">{page.introText2}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="partners-page-categories animate-on-scroll">
        <div className="container">
          {grouped.map((cat) => (
            <article key={cat.id} className="partners-page-category">
              <div className="partners-page-category-head">
                <span className="partners-page-category-label">{cat.name}</span>
                <div className="partners-page-category-line" />
                <span className="partners-page-category-count">{cat.logos.length} partners</span>
              </div>
              <div className="partners-page-logo-grid">
                {cat.logos.map((partner) => {
                  const imageSrc = resolveImage(partner.image);
                  const partnerData = {
                    imageSrc,
                    name: partner.name,
                    hoverText: partner.hoverText || `Authorized ${partner.name} partner for ${cat.name} solutions.`,
                    category: cat.name,
                    link: partner.link || "",
                  };

                  const inner = (
                    <>
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt=""
                          loading="lazy"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement?.classList.add("is-fallback", "is-name-only");
                          }}
                        />
                      ) : null}
                      <span className="partners-page-logo-fallback">{partner.name}</span>
                    </>
                  );

                  const className = `partners-page-logo${!imageSrc ? " is-fallback is-name-only" : ""}${partner.link ? " is-link" : ""}`;

                  if (partner.link) {
                    return (
                      <a
                        key={partner.id}
                        href={partner.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={className}
                        onMouseEnter={() => showPartner(partnerData)}
                        onMouseLeave={hidePartner}
                        onFocus={() => showPartner(partnerData)}
                        onBlur={hidePartner}
                        aria-label={`${cat.name} partner — ${partner.name}`}
                      >
                        {inner}
                      </a>
                    );
                  }

                  return (
                    <button
                      key={partner.id}
                      type="button"
                      className={className}
                      onMouseEnter={() => showPartner(partnerData)}
                      onMouseLeave={hidePartner}
                      onFocus={() => showPartner(partnerData)}
                      onBlur={hidePartner}
                      aria-label={`${cat.name} partner — ${partner.name}`}
                    >
                      {inner}
                    </button>
                  );
                })}
              </div>
            </article>
          ))}
          {loading ? (
            <p className="cms-empty text-center py-5">Loading partners…</p>
          ) : grouped.length === 0 ? (
            <p className="cms-empty text-center py-5">
              {fromDb
                ? "No partners in database yet. Add them from Admin → Partners (or click Load all logos to DB)."
                : "Partner logos will appear here once the API is available."}
            </p>
          ) : null}
        </div>
      </section>

      <SiteCta
        title={page.ctaTitle}
        desc={page.ctaDesc}
        buttonText={page.ctaButton || "Contact Us"}
        to="/contact"
      />

      {activePartner ? (
        <div className="partners-page-popup-overlay" aria-live="polite">
          <div className="partners-page-popup">
            <div className="partners-page-popup-logo">
              {activePartner.imageSrc ? (
                <img src={activePartner.imageSrc} alt="" />
              ) : (
                <span className="partners-page-popup-name">{activePartner.name}</span>
              )}
            </div>
            <span className="partners-page-popup-category">{activePartner.category}</span>
            <p className="partners-page-popup-title">{activePartner.name}</p>
            <p className="partners-page-popup-text">{activePartner.hoverText}</p>
            {activePartner.link ? (
              <p className="partners-page-popup-link">Visit website →</p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
